import type { FC, MouseEvent } from 'react'
import { memo, useEffect, useState } from 'react'

import { type SpacingProps } from '@material-ui/system'
import { debounce, throttle } from 'lodash'

import { PaginationLabelStyled } from './styles/PaginationLabelStyled'
import { PaginationStyled } from './styles/PaginationStyled'
import { PaginationTextFieldStyled } from './styles/PaginationTextFieldStyled'
import { IconButton } from '../../elements/Button'
import { InputLabel } from '../Form'
import { IconChevronLeft, IconChevronRight } from '@ui/elements/Icon'

export interface IPaginationProps extends SpacingProps {
  currentPage?: number
  numPages?: number
  paginationLabel?: string
  onPageChange?(currentPage: number, e?: MouseEvent<HTMLElement>): void
}

export const Pagination: FC<IPaginationProps> = memo((props) => {
  const {
    currentPage,
    numPages,
    paginationLabel = 'Page',
    onPageChange,
    ...otherProps
  } = props
  const [pageValue, setPageValue] = useState<number | ''>(currentPage || 1)

  useEffect(() => {
    if (pageValue !== currentPage) {
      setPageValue(currentPage)
    }
  }, [currentPage])

  const handlePageChange = debounce((validatedInput, prevCurrentPage) => {
    setPageValue(validatedInput)
    if (validatedInput !== prevCurrentPage) {
      onPageChange(validatedInput)
    }
  }, 250)

  const handlePageClick = throttle((validatedInput) => {
    onPageChange(validatedInput)
  }, 250)

  const handlePreviousClick = () => {
    const validatedInput = validateInput(+pageValue - 1)
    if (validatedInput !== pageValue) {
      setPageValue(validatedInput)
      handlePageClick(validatedInput)
    }
  }

  const handleNextClick = () => {
    const validatedInput = validateInput(+pageValue + 1)
    if (validatedInput !== pageValue) {
      setPageValue(validatedInput)
      handlePageClick(validatedInput)
    }
  }

  const handleInputChange = (event) => {
    if (event.target.value === '') {
      setPageValue(event.target.value)
    } else {
      const prevCurrentPage = pageValue
      const validatedInput = validateInput(event.target.value)
      setPageValue(validatedInput)
      handlePageChange(validatedInput, prevCurrentPage)
    }
  }

  const handleDefocus = () => {
    if (pageValue === '') {
      setPageValue(1)
      onPageChange(1)
    }
  }

  const validateInput = (input) => {
    if (isNaN(input)) {
      return pageValue
    }

    if (input < 1) {
      return 1
    }

    if (input > numPages) {
      return numPages
    }

    return Number(input)
  }

  return (
    <PaginationStyled {...otherProps}>
      <IconButton
        aria-label='Go to previous page'
        className='previous-button'
        iconComponent={<IconChevronLeft />}
        disabled={currentPage === 1 || pageValue === 1}
        onClick={handlePreviousClick}
        type='button'
      />
      <PaginationLabelStyled>
        <InputLabel display='inline' htmlFor='currentPageInput'>
          {paginationLabel}
        </InputLabel>
      </PaginationLabelStyled>
      <PaginationTextFieldStyled
        className='input-field'
        onBlur={handleDefocus}
        value={pageValue}
        onChange={handleInputChange}
        id='currentPageInput'
      />
      <PaginationLabelStyled>of {numPages}</PaginationLabelStyled>
      <IconButton
        aria-label='Go to next page'
        className='next-button'
        iconComponent={<IconChevronRight />}
        disabled={currentPage === numPages || pageValue === numPages}
        onClick={handleNextClick}
        type='button'
      />
    </PaginationStyled>
  )
})

Pagination.defaultProps = { currentPage: 1, numPages: 1 }
Pagination.displayName = 'Pagination'
